import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Radio,
    RadioGroup,
    FormControlLabel,
    TextField,
    Typography,
    Box,
    CircularProgress,
    Link,
    Grid
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../utils/languageContext';
import { toast } from "react-toastify";
import { uwinAPI } from "../../../services/uwin.api";
import Loader from '../../../components/Loader'

const UpdateHbValue = (props) => {
    const { showUpdateHbValue, setShowUpdateHbValue, setHBTestingDate, benProfileData } = props;
    const { t } = useLanguage();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [HbValue, setHbValue] = useState('');
    const [testingDate, setTestingDate] = useState('');
    const [isCounselling, setIsCounselling] = useState('');
    const [isDeliveryInLastSixMonths, setIsDeliveryInLastSixMonths] = useState('');
    const [isChildAlive, setIsChildAlive] = useState('');
    const [showRadioButtons, setShowRadioButtons] = useState(false);

    const resetState = () => {
        setIsLoading(false);
        setHbValue("");
        setTestingDate("");
        setIsCounselling("");
        setIsDeliveryInLastSixMonths("");
        setIsChildAlive("");
        setShowRadioButtons(false);
    };

    const isEligibleFemaleCitizen = () => {
        if (!benProfileData) return false;

        if (benProfileData.beneficiary_gender !== "Female") return false;

        const dob = new Date(benProfileData.dob);
        if (isNaN(dob)) return false;

        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();

        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            age--;
        }

        if (age < 12 || age > 49) return false;

        if (benProfileData.beneficiary_type_category_name !== "Citizen") return false;

        if (benProfileData.is_pregnant_woman === true) return false;

        setShowRadioButtons(true);
    }

    useEffect (() => {  
        isEligibleFemaleCitizen();
    }, [benProfileData]);

    const handleSubmit = async () => {
        if (!testingDate.trim()) {
            toast.error("Please enter testing date");
            return;
        } else if (!HbValue.trim()) {
            toast.error("Please enter HB value");
            return;
        } else if (!isCounselling.trim()) {
            toast.error("Please select counselling option");
            return;
        }
        try {
            setIsLoading(true);
            const payload = {
                beneficiary_id: benProfileData.beneficiary_id,
                hb_value: HbValue,
                hb_testing_date: testingDate,
                counselling_done: isCounselling,
                is_delivery_in_last_six_months: "",
                is_child_alive: ""
            }
            if(showRadioButtons) {
                payload.is_delivery_in_last_six_months = isDeliveryInLastSixMonths;
                payload.is_child_alive = isChildAlive;
            }
            // Fetch the parent profile data
            const updateHBValue = await uwinAPI.saveHemoglobinDetail(payload);

            if (updateHBValue.success) {
                setIsLoading(false);
                toast.success("HB Value updated successfully");
                setShowUpdateHbValue(false);
                setHBTestingDate(testingDate);
            }
        } catch (error) {
            if (error?.response?.data) {
                toast.error(error.response.data.message.error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {isLoading && <Loader />}
            <Dialog
                open={showUpdateHbValue}
                onClose={() => setShowUpdateHbValue(false)}
                maxWidth="md"
                fullWidth 
                PaperProps = {{ sx: { borderRadius: "25px", p: 1 } }}
            >
                <DialogTitle>
                    Update HB Value
                    <IconButton  onClick={() => setShowUpdateHbValue(false)} sx={{ position: "absolute", right: 16, top: 16 }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: 4, pb: 4, borderTop: "1px solid #9c9c9c" }}>
                    <Grid container spacing={2} sx={{ justifyContent: "center", alignItems: "center", mt: 2 }}>
                        <Grid size={6}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Date of HB Testing *
                            </Typography>
                            <TextField fullWidth type="date" value={testingDate} onChange={(e) => setTestingDate(e.target.value)} />
                        </Grid>
                        <Grid size={6}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                HB Value (g/dL) *
                            </Typography>
                            <TextField fullWidth type="number" value={HbValue} onChange={(e) => setHbValue(e.target.value)}/>
                        </Grid>
                        { showRadioButtons && (
                            <>
                                <Grid size={6}>
                                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                        Delivered in last six months? *
                                    </Typography>
                                    <RadioGroup row value={isDeliveryInLastSixMonths} onChange={(e) => setIsDeliveryInLastSixMonths(e.target.value)} sx={{ mb: 2 }} >
                                        <FormControlLabel value="Yes" control={<Radio size="small" />} checked={isCounselling === "Yes"}   label="Yes" />
                                        <FormControlLabel value="No" control={<Radio size="small" />}  checked={isCounselling === "No"} label="No" />
                                    </RadioGroup>
                                </Grid>
                                <Grid size={6}>
                                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                        Child is alive? *
                                    </Typography>
                                    <RadioGroup row value={isChildAlive} onChange={(e) => setIsChildAlive(e.target.value)} sx={{ mb: 2 }} >
                                        <FormControlLabel value="Yes" control={<Radio size="small" />}  checked={isCounselling === "Yes"}  label="Yes" />
                                        <FormControlLabel value="No" control={<Radio size="small" />}  checked={isCounselling === "No"} label="No" />
                                    </RadioGroup>
                                </Grid>
                            </>
                        )}
                        <Grid size={12}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Whether Counselling has been done? *
                            </Typography>
                            <RadioGroup row value={isCounselling} onChange={(e) => setIsCounselling(e.target.value)} sx={{ mb: 2 }} >
                                <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" checked={isCounselling === "Yes"} />
                                <FormControlLabel value="No" control={<Radio size="small" />} label="No" checked={isCounselling === "No"} />
                            </RadioGroup>
                        </Grid>
                        <Grid size={8} sx={{ mt: 2 }}>
                        </Grid>
                        <Grid size={4} sx={{ textAlign: "right", mt: 2 }}>
                            <Button variant="contained" color="danger" onClick={() => setShowUpdateHbValue(false) && resetState()} sx={{ mr: 2 }}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="primary" onClick={handleSubmit}>
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default UpdateHbValue;